/**
 * document.title source for the superadmin app.
 *
 * The title is derived from the SAME nav config that drives the sidebar, the
 * header module title and the subtab strip (see components/nav/navConfig.js),
 * so the tab title can never drift from what's on screen. It reads:
 *
 *     Admin - <Module> · <Tab> | Bluetail
 *
 * with the "· <Tab>" segment added whenever the active module has a subtab (or,
 * for the nested Pipelines module, a child stage) distinct from the module name.
 *
 * The env designator ([LOCAL] / [QA] / [STG]) is added on top by setTitle() in
 * DocumentHelper based on REACT_APP_ENV — do not bake it in here. Net result on
 * a non-prod env is "[QA] Admin - <Module> · <Tab> | Bluetail" — i.e. always in
 * sync with [env + module + tab].
 */
import { resolveActiveModule, resolveActiveSubtab } from './components/nav/navConfig';

const DEFAULT_TITLE = "Admin | Bluetail";

// Routes that are not part of the flattened nav (pre-auth + standalone pages).
// First match wins.
const STATIC_TITLES = [
  { match: /^\/logout\b/, label: "Logout" },
  { match: /^\/changepassword\b/, label: "Change Password" },
  { match: /^\/$/, label: "Login" },
  { match: /^\/home\/Profile\/MyProfile\b/, label: "My Profile" },
  { match: /^\/home\/notfound\b/, label: "Not Found" },
];

// The active leaf within a module: its subtab, or — for the nested Pipelines
// module — its active child stage. Returns null for a plain single-view module.
function activeLeafLabel(module, pathname) {
  const subtab = resolveActiveSubtab(module, pathname);
  if (subtab) return subtab.label;

  if (module.nested && Array.isArray(module.children)) {
    const child = module.children.find(
      (c) => pathname === c.path || pathname.startsWith(`${c.path}/`),
    );
    if (child) return child.label;
  }

  return null;
}

export function getTitleForPath(pathname) {
  if (!pathname) return DEFAULT_TITLE;

  const module = resolveActiveModule(pathname);
  if (module) {
    const moduleLabel = module.title || module.label;
    const leaf = activeLeafLabel(module, pathname);
    // Collapse "Accounts · Accounts" (module == default subtab) to just "Accounts".
    const label = leaf && leaf !== moduleLabel ? `${moduleLabel} · ${leaf}` : moduleLabel;
    return `Admin - ${label} | Bluetail`;
  }

  const staticMatch = STATIC_TITLES.find((t) => t.match.test(pathname));
  if (staticMatch) return `Admin - ${staticMatch.label} | Bluetail`;

  return DEFAULT_TITLE;
}

export { DEFAULT_TITLE };
